/**
 * GroundingNote (P153) -- the visible grounded-AI verdict.
 *
 * Renders under a retrieve-then-judge Engine result list. When the
 * Engine's citations were all on the shortlist, a calm note saying so;
 * when it invented sources that were dropped, an amber honesty signal.
 * Renders nothing when the Engine returned no items.
 *
 * Round 12 D1 + D4 -- what this checks is that the Engine pointed at
 * something that exists. It does not check that it pointed at the right
 * thing, and it never could: every guard in the product is an
 * identifier check. The word "grounded" reads as "accurate" and means
 * "cited", so the line says what it verified and a second line says
 * what it did not.
 *
 * And a citation drawn from a shortlist of ONE is not a judgement at
 * all. Five drafted actions on a CAP with one linked control all cited
 * that control -- three of them about something else entirely -- over a
 * line reading "Grounded: all 5 results cite real sources."
 */
import { useTranslation } from "react-i18next";

import type { EngineGrounding } from "shared/api/types";

export function GroundingNote({
  grounding,
}: {
  grounding: EngineGrounding | null | undefined;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (!grounding || grounding.returned === 0) return null;
  const clean = grounding.dropped === 0;
  // P232: hard-refusal dropped the whole result (below the floor) -- the
  // strongest signal, takes precedence over the P173 low-confidence note.
  const refused = grounding.refused === true;
  // P173: when the Engine invented more than it grounded, escalate from
  // the calm "some dropped" note to a low-confidence warning.
  const lowConfidence = grounding.below_floor === true;
  // D1 -- the model had no choice. Amber: not an error, and not a check.
  const nominal = grounding.nominal_attribution === true;
  const tone =
    refused || lowConfidence
      ? "text-rag-red font-medium"
      : nominal || !clean
        ? "text-rag-amber"
        : "text-slate-400";
  return (
    <div
      data-testid="engine-grounding-note"
      data-dropped={grounding.dropped}
      data-low-confidence={lowConfidence ? "true" : "false"}
      data-nominal={nominal ? "true" : "false"}
      data-refused={refused ? "true" : "false"}
      className={`mt-2 text-[11px] ${tone}`}
    >
      <p>
        {refused
          ? t("common.grounding.refused")
          : lowConfidence
            ? t("common.grounding.lowConfidence", {
                grounded: grounding.grounded,
                returned: grounding.returned,
              })
            : nominal
              ? t("common.grounding.nominalAttribution", {
                  count: grounding.returned,
                })
              : clean
                ? t("common.grounding.allGrounded", { count: grounding.grounded })
                : t("common.grounding.dropped", { count: grounding.dropped })}
      </p>
      {/* D4 -- said on every non-refused result, because the thing a
          reader needs to know is constant: the check was of the
          identifier, not of the sentence. */}
      {!refused && (
        <p data-testid="engine-grounding-caveat" className="mt-0.5 text-slate-500">
          {t("common.grounding.citedNotChecked")}
        </p>
      )}
    </div>
  );
}
